import { apiService, type ApiResponse } from "./api";
import { getOkrResultColumnLabel } from "../utils/cycleUtils";

// Core data structures
export interface OKR {
  objective: string;
  target: string;
  eyr: string; // end year result
  rating: number | null;
}

// Component OKR structure (used in React components)
export interface ComponentOKR {
  id: string;
  objective: string;
  target: string;
  endYearResult: string;
  rating: number | null;
}

export interface Employee {
  emp_id: number | string;
  id:number;
  email: string;
  full_name: string;
  isExternal?: boolean;
  external?: boolean;
}

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

// API request/response interfaces
export interface SelfAppraisalResponse {
  id: number;
  email: string;
  is_freezed: boolean;
  comment?: string; // Manager's comment for OKR rejection
  status?: string; // OKR status (e.g., 'approved', 'rejected', 'pending')
  immediate_manager: string;
  immediate_manager_email?: string; // Add email field for tooltip
  self_appraisal_end_date: string;
  self_appraisal_cycle_left: number;
  okrs: OKR[];
  // Support alternative response structure
  self_appraisal_list?: {
    id: number;
    okrs: OKR[];
    status: string;
    rejected_remarks?: string;
  };
  // Employee details from OKR API
  employee?: {
    email: string;
    full_name: string;
    department_name: string;
    manager_name: string;
    designation: string;
    employee_id: string;
    is_active: boolean;
  };
}

export interface UpdateSelfAppraisalRequest {
  id: number;
  email: string;
  okrs: OKR[];
  cycle_type?: number; // 0=START, 1=MID, 2=END
}

export interface SubmitSelfAppraisalRequest {
  id: number;
  email: string;
  isFreezed: boolean;
  okrs: OKR[];
  cycle_type?: number; // 0=START, 1=MID, 2=END
}

// Nomination interfaces
export interface NominationEntry {
  id: number | null;
  email: string;
  external: boolean;
}

export interface NominationRequest {
  email: string;
  nominations: {
    peer: NominationEntry[];
    subordinate: NominationEntry[];
    additional_subordinate: NominationEntry[];
    superior: NominationEntry[];
    stakeholder: NominationEntry[];
    dotted_superior: NominationEntry[];
  };
}

export interface NominationResponse {
  cycle_id: number;
  nomination_manager: Employee[];
  nomination_end_date: string;
  nomination_cycle_left: number;
  superiors: Employee[];
  dotted_superior: Employee[];
  dotted_superiors?: Employee[];
  subordinates: Employee[];
  additional_subordinate?: Employee[];
  additional_subordinates?: Employee[];
  peers: Employee[];
  stakeholders: Employee[];
}

export interface SubmitNominationResponse {
  nomination_id: number;
  status: string;
}

export interface OKRActionResponse {
  success: boolean;
  message: string;
  okr_id: number;
  status: string;
}

export interface UpdatedNominationResponse {
  nomination_id: number;
  status: string;
  nominations: {
    peer: Employee[];
    subordinate: Employee[];
    additional_subordinate?: Employee[];
    additional_subordinates?: Employee[];
    superior: Employee[];
    stakeholder: Employee[];
    dotted_superior: Employee[];
  };
  manager_comments: string;
  approved_by: string;
  approved_at: string;
  nomination_start_date: string;
  nomination_end_date: string;
  nomination_cycle_left: number;
}

class SelfAppraisalService {
  private readonly baseEndpoint = "/api/v1/feedback/self_appraisal";

  // Nomination methods
  async getNominationsDefault(email: string): Promise<ApiResponse<NominationResponse>> {
    try {
      const response = await apiService.get<NominationResponse>(
        `/api/v1/feedback/nomination/`,
        { params: { email } }
      );
      return response;
    } catch (error) {
      console.error("Error fetching nominations:", error);
      throw error;
    }
  }
  async getUpdatedNominations(email: string): Promise<ApiResponse<UpdatedNominationResponse>> {
    try {
      const response = await apiService.get<UpdatedNominationResponse>(
        `api/v1/feedback/nomination/details/`,
        { params: { email } }
      );
      return response;
    } catch (error) {
      console.error("Error fetching nominations:", error);
      throw error;
    }
  }

  async updateNominations(data: NominationRequest): Promise<ApiResponse<SubmitNominationResponse>> {
    try {
      const response = await apiService.put<SubmitNominationResponse>(
        `/api/v1/feedback/nomination/details/`,
        data
      );
      return response;
    } catch (error) {
      console.error("Error updating nominations:", error);
      throw error;
    }
  }

  async submitNomination(data: NominationRequest): Promise<ApiResponse<SubmitNominationResponse>> {
    try {
      const response = await apiService.post<SubmitNominationResponse>(
        `/api/v1/feedback/nomination/details/`,
        data
      );
      return response;
    } catch (error) {
      console.error("Error submitting nominations:", error);
      throw error;
    }
  }

  // Self appraisal methods
  async getSelfAppraisal(email: string): Promise<ApiResponse<SelfAppraisalResponse>> {
    try {
      const response = await apiService.get<SelfAppraisalResponse>(
        `${this.baseEndpoint}/?email=${encodeURIComponent(email)}`
      );
      return response;
    } catch (error) {
      console.error("Error fetching self appraisal:", error);
      throw error;
    }
  }

  async updateSelfAppraisal(data: UpdateSelfAppraisalRequest): Promise<ApiResponse<SelfAppraisalResponse>> {
    try {
      const response = await apiService.put<SelfAppraisalResponse>(
        `${this.baseEndpoint}/`,
        data
      );
      return response;
    } catch (error) {
      console.error("Error updating self appraisal:", error);
      throw error;
    }
  }

  async submitSelfAppraisal(data: SubmitSelfAppraisalRequest): Promise<ApiResponse<SelfAppraisalResponse>> {
    try {
      const response = await apiService.post<SelfAppraisalResponse>(
        `${this.baseEndpoint}/`,
        data
      );
      return response;
    } catch (error) {
      console.error("Error submitting self appraisal:", error);
      throw error;
    }
  }

  // Data transformation utilities
  transformOKRsToApiFormat(okrs: ComponentOKR[]): OKR[] {
    return okrs.map((okr) => ({
      objective: okr.objective,
      target: okr.target,
      eyr: okr.endYearResult,
      rating: okr.rating??0,
    }));
  }

  transformOKRsFromApiFormat(okrs: OKR[]): ComponentOKR[] {
    return okrs.map((okr, index) => ({
      id: `okr-${index + 1}`,
      objective: okr.objective,
      target: okr.target,
      endYearResult: okr.eyr,
      rating: okr.rating ?? null,
    }));
  }

  // Convenience methods for common operations
  async saveDraft(email: string, id: number, okrs: ComponentOKR[], cycle_type?: number): Promise<ApiResponse<SelfAppraisalResponse>> {
    const transformedOKRs = this.transformOKRsToApiFormat(okrs);
    return this.updateSelfAppraisal({ id, email, okrs: transformedOKRs, cycle_type });
  }

  async submitFinalAppraisal(email: string, id: number, okrs: ComponentOKR[], cycle_type?: number): Promise<ApiResponse<SelfAppraisalResponse>> {
    const transformedOKRs = this.transformOKRsToApiFormat(okrs);
    return this.submitSelfAppraisal({ id, email, isFreezed: true, okrs: transformedOKRs, cycle_type });
  }

  // Helper methods
  isEditable(data: SelfAppraisalResponse): boolean {
    return !data.is_freezed;
  }

  getDaysLeft(data: SelfAppraisalResponse): number {
    return data.self_appraisal_cycle_left;
  }

  getFormattedEndDate(data: SelfAppraisalResponse): string {
    const date = new Date(data.self_appraisal_end_date);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }

  /**
   * @param cycleType Optional cycle phase (0=START, 1=MID, 2=END).
   * When START, result and Rating fields are not required.
   */
  validateOKRs(okrs: ComponentOKR[], cycleType?: number): ValidationResult {
    const errors: string[] = [];

    if (okrs.length === 0) {
      errors.push("At least one OKR is required");
      return { isValid: false, errors };
    }

    // Check for duplicate objectives
    const objectiveTexts = okrs.map(okr => okr.objective.trim().toLowerCase());
    const duplicateObjectives = new Set<string>();
    const seenObjectives = new Set<string>();
    
    objectiveTexts.forEach((objective) => {
      if (objective && seenObjectives.has(objective)) {
        duplicateObjectives.add(objective);
      } else if (objective) {
        seenObjectives.add(objective);
      }
    });

    okrs.forEach((okr, index) => {
      const okrNumber = index + 1;

      if (!okr.objective.trim()) {
        errors.push(`OKR ${okrNumber}: Objective is required`);
      } else if (duplicateObjectives.has(okr.objective.trim().toLowerCase())) {
        errors.push(`OKR ${okrNumber}: Duplicate objective detected. Each objective must be unique`);
      }

      if (!okr.target.trim()) {
        errors.push(`OKR ${okrNumber}: Target is required`);
      }

      // Result and Rating are required for MID (1) and END (2), but not START (0).
      const isTargetSettingsCycle = cycleType === 0;
      const eyrRatingRequired = cycleType === undefined || !isTargetSettingsCycle;
      const resultLabel = getOkrResultColumnLabel(cycleType);

      if (eyrRatingRequired && !okr.endYearResult.trim()) {
        errors.push(`OKR ${okrNumber}: ${resultLabel} is required`);
      }

      if (eyrRatingRequired && (okr.rating == null || okr.rating < 1 || okr.rating > 4)) {
        errors.push(`OKR ${okrNumber}: Rating must be between 1 and 4`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Get OKR details for a specific employee (for manager view)
   * 
   * @param okrId - OKR ID
   * @param managerEmail - Manager's email address
   * @returns Promise with OKR details
   */
  async getOKRDetails(okrId: number, managerEmail: string): Promise<ApiResponse<SelfAppraisalResponse>> {
    try {
      const url = `/api/v1/feedback/self_appraisal/details/?okr_id=${okrId}&manager_email=${encodeURIComponent(managerEmail)}`;
      
      console.log('🔄 Fetching OKR details:', { okrId, managerEmail, url });

      const response = await apiService.get<SelfAppraisalResponse>(url);
      
      console.log('✅ OKR details response:', response);
      
      return response;
    } catch (error) {
      console.error('❌ Error fetching OKR details:', error);
      throw error;
    }
  }

  /**
   * Approve or reject OKR self-appraisal
   * 
   * @param okrId - OKR ID
   * @param managerEmail - Manager's email address
   * @param action - 'approve' or 'reject'
   * @param comments - Optional comments (required for rejection)
   * @returns Promise with API response
   */
  async submitOKRAction(
    okrId: number,
    managerEmail: string,
    action: 'approved' | 'rejected',
    comments?: string
  ): Promise<ApiResponse<OKRActionResponse>> {
    try {
      const url = '/api/v1/feedback/self_appraisal/action/';
      
      const payload = {
        okr_id: okrId,
        manager_email: managerEmail,
        action: action,
        ...(comments && { comments })
      };

      console.log('🔄 Submitting OKR action:', payload);

      const response = await apiService.put<OKRActionResponse>(url, payload);
      
      console.log('✅ OKR action response:', response);
      
      return response;
    } catch (error) {
      console.error('❌ Error submitting OKR action:', error);
      throw error;
    }
  }
}

export const selfAppraisalService = new SelfAppraisalService();
export default selfAppraisalService;
